import React, { useEffect, useState, useCallback } from "react";
import { api, mediaSrc, fmtTime, fmtDate } from "../api";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { PhotoViewer } from "./PhotoViewer";
import { CompareView } from "./CompareView";
import { ConfirmDeleteDialog } from "./Artifacts";
import { toast } from "sonner";
import { GitCompareArrows, X, Trash2, CheckSquare, Square, Play } from "lucide-react";
import { MediaSelectBar, SelectMark } from "./MediaSelectBar";
import { mediaFilename } from "../lib/share";
import { VideoViewer } from "./VideoViewer";

const CATEGORIES = ["before", "during", "issue", "correction", "after"];
const catColor = {
  before: "bg-muted text-foreground",
  during: "bg-primary/15 text-primary",
  issue: "bg-destructive/15 text-destructive",
  correction: "bg-accent/25 text-accent-foreground",
  after: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
};

export function PhotoTimeline({ jobId, conversation }) {
  const { user, t, lang } = useApp();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [pickMode, setPickMode] = useState(false); // multi-select → save/share many
  const [picked, setPicked] = useState([]);
  const [videoOpen, setVideoOpen] = useState(null);
  const togglePick = (m) => setPicked((p) => (p.includes(m.id) ? p.filter((x) => x !== m.id) : [...p, m.id]));
  const [selected, setSelected] = useState([]);
  const [compare, setCompare] = useState(null);
  const [deletePhoto, setDeletePhoto] = useState(null); // ADMIN moderation: photo pending delete confirmation
  const isAdmin = user?.role === "admin" || !!user?.super_admin;

  const doDeletePhoto = async () => {
    if (!deletePhoto) return;
    try {
      await api.delete(`/media/${deletePhoto.id}`);
      toast.success(t("deleted"));
      setDeletePhoto(null);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const load = useCallback(async () => {
    const res = await api.get(`/jobs/${jobId}/photos${filter ? `?category=${filter}` : ""}`);
    setData(res.data);
  }, [jobId, filter]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (photo) => {
    setSelected((prev) => {
      if (prev.find((p) => p.id === photo.id)) return prev.filter((p) => p.id !== photo.id);
      if (prev.length >= 2) return [prev[1], photo];
      return [...prev, photo];
    });
  };

  useEffect(() => {
    if (selected.length === 2) {
      setCompare({ left: selected[0], right: selected[1] });
      setCompareMode(false);
      setSelected([]);
    }
  }, [selected]);

  if (!data) return <div className="h-48 bg-muted rounded-xl animate-pulse" />;

  return (
    <div data-testid="photo-timeline">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex gap-1.5 flex-wrap">
          <button
            data-testid="photo-filter-chip-all"
            onClick={() => setFilter(null)}
            className={`rounded-full px-3 py-1.5 text-xs border font-medium transition-colors ${!filter ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted"}`}
          >
            {t("all")} · {data.total}
          </button>
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              data-testid={`photo-filter-chip-${cat}`}
              onClick={() => setFilter(filter === cat ? null : cat)}
              className={`rounded-full px-3 py-1.5 text-xs border font-medium transition-colors ${filter === cat ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted"}`}
            >
              {t(cat === "issue" ? "issue_cat" : cat)} · {data.counts[cat] || 0}
            </button>
          ))}
        </div>
        <Button
          size="sm"
          variant={pickMode ? "default" : "outline"}
          data-testid="media-select-button"
          onClick={() => { setPickMode((m) => !m); setPicked([]); setCompareMode(false); setSelected([]); }}
        >
          {pickMode ? <X className="h-4 w-4 mr-1.5" /> : <CheckSquare className="h-4 w-4 mr-1.5" />}
          {pickMode ? t("cancel") : t("select_items")}
        </Button>
        <Button
          size="sm"
          variant={compareMode ? "default" : "outline"}
          data-testid="photo-compare-button"
          onClick={() => { setCompareMode((m) => !m); setSelected([]); setPickMode(false); setPicked([]); }}
        >
          {compareMode ? <X className="h-4 w-4 mr-1.5" /> : <GitCompareArrows className="h-4 w-4 mr-1.5" />}
          {compareMode ? t("cancel") : t("compare")}
        </Button>
      </div>

      {pickMode && (
        <MediaSelectBar
          className="sticky top-1 z-20 mb-3"
          items={data.photos.filter((m) => picked.includes(m.id)).map((m) => ({ id: m.id, url: mediaSrc(m), filename: mediaFilename(m, conversation?.job), kind: m.kind }))}
          onCancel={() => { setPickMode(false); setPicked([]); }}
          onDelete={isAdmin ? async () => {
            const res = await Promise.allSettled(picked.map((id) => api.delete(`/media/${id}`)));
            const failed = res.filter((r) => r.status === "rejected").length;
            if (failed) toast.error(`${failed}/${picked.length} Failed`); else toast.success(t("deleted"));
            load();
          } : undefined}
        />
      )}
      {compareMode && (
        <div className="text-xs text-muted-foreground mb-3 bg-accent/15 border border-accent/40 rounded-lg px-3 py-2">
          {t("compare_select")} ({selected.length}/2)
        </div>
      )}

      {data.photos.length === 0 && (
        <div className="text-center text-sm text-muted-foreground py-16">{t("no_photos")}</div>
      )}

      {/* Photos grouped by DAY (newest day first) for quick progress review */}
      {(() => {
        const dayGroups = [];
        data.photos.forEach((photo) => {
          const day = fmtDate(photo.created_at, lang);
          const last = dayGroups[dayGroups.length - 1];
          if (!last || last.day !== day) dayGroups.push({ day, photos: [photo] });
          else last.photos.push(photo);
        });
        dayGroups.reverse(); // newest day on top; photos inside stay chronological
        return dayGroups.map((g) => {
          const dayIds = g.photos.map((p) => p.id);
          const allDay = dayIds.every((id) => picked.includes(id));
          const toggleDay = () => {
            if (!pickMode) { setPickMode(true); setCompareMode(false); setSelected([]); }
            setPicked((p) => (allDay ? p.filter((id) => !dayIds.includes(id)) : [...p, ...dayIds.filter((id) => !p.includes(id))]));
          };
          return (
          <div key={g.day} className="mb-6" data-testid="photo-day-group">
            <div className="flex items-center gap-3 mb-2.5">
              <div className="h-px bg-border flex-1" />
              <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide" data-testid="photo-day-header">
                {g.day} · {g.photos.length} {g.photos.length === 1 ? t("media_one") : t("media_many")}
              </span>
              <div className="h-px bg-border flex-1" />
              <button
                type="button"
                onClick={toggleDay}
                aria-label={allDay ? t("deselect_day") : t("select_day")}
                title={allDay ? t("deselect_day") : t("select_day")}
                data-testid="photo-day-select-all"
                className={`h-7 w-7 rounded-md border flex items-center justify-center shrink-0 transition-colors ${allDay ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground hover:bg-muted"}`}
              >
                {allDay ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {g.photos.map((photo) => {
                const isSel = selected.find((p) => p.id === photo.id);
                return (
                  <div
                    key={photo.id}
                    data-testid="photo-card"
                    onClick={() => (pickMode ? togglePick(photo) : compareMode ? toggleSelect(photo) : photo.kind === "video" ? setVideoOpen(photo) : setViewer(photo))}
                    className={`overflow-hidden rounded-xl border bg-card cursor-pointer group relative transition-all ${isSel ? "ring-2 ring-primary" : ""}`}
                  >
                    <div className="aspect-square overflow-hidden relative">
                      {photo.kind === "video" ? (
                        <>
                          <video src={mediaSrc(photo) + (photo.poster ? "" : "#t=0.1")} poster={photo.poster || undefined} muted preload="metadata" playsInline className="w-full h-full object-cover bg-black pointer-events-none" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="h-9 w-9 rounded-full bg-black/60 flex items-center justify-center"><Play className="h-4 w-4 text-white ml-0.5" /></div>
                          </div>
                        </>
                      ) : (
                        <img src={mediaSrc(photo)} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-200" />
                      )}
                    </div>
                    <div className="absolute top-2 left-2 flex gap-1">
                      {photo.category && (
                        <Badge className={`${catColor[photo.category]} text-[9px] px-1.5 border-0`}>
                          {t(photo.category === "issue" ? "issue_cat" : photo.category)}
                        </Badge>
                      )}
                      {photo.version > 1 && <Badge className="bg-black/60 text-white text-[9px] px-1.5 border-0">v{photo.version}</Badge>}
                    </div>
                    {pickMode && <SelectMark selected={picked.includes(photo.id)} index={picked.indexOf(photo.id) + 1} />}
                    {isAdmin && !compareMode && !pickMode && (
                      <button
                        type="button"
                        data-testid="photo-admin-delete-btn"
                        title={t("delete")}
                        onClick={(e) => { e.stopPropagation(); setDeletePhoto(photo); }}
                        className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/55 text-white flex items-center justify-center opacity-80 hover:opacity-100 hover:bg-destructive transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <div className="px-2 py-1.5 text-[10px] text-muted-foreground">
                      {fmtTime(photo.created_at)}
                    </div>
                    {isSel && (
                      <div className="absolute inset-0 bg-primary/25 flex items-center justify-center">
                        <div className="h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                          {selected.findIndex((p) => p.id === photo.id) + 1}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          );
        });
      })()}

      {viewer && (() => {
        const photos = (data.photos || []).filter((p) => p.kind !== "video");
        const idx = photos.findIndex((p) => p.id === viewer.id);
        return (
          <PhotoViewer
            key={viewer.id}
            open={!!viewer}
            media={viewer}
            conversation={conversation}
            onClose={() => { setViewer(null); load(); }}
            onPrev={idx > 0 ? () => setViewer(photos[idx - 1]) : undefined}
            onNext={idx >= 0 && idx < photos.length - 1 ? () => setViewer(photos[idx + 1]) : undefined}
            gallery={photos.map((p) => ({ media: p, message: null }))}
            onSelectItem={(g) => setViewer(g.media)}
          />
        );
      })()}
      {videoOpen && <VideoViewer open media={videoOpen} conversation={conversation} markups={[]} onClose={() => setVideoOpen(null)} />}
      {compare && (
        <CompareView open={!!compare} left={compare.left} right={compare.right} onClose={() => setCompare(null)} />
      )}
      <ConfirmDeleteDialog
        open={!!deletePhoto}
        onClose={() => setDeletePhoto(null)}
        onConfirm={doDeletePhoto}
        title={t("delete_photo_confirm")}
        t={t}
        testid="photo"
      />
    </div>
  );
}
